import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { RiMailLine, RiMailOpenLine, RiReplyLine, RiDeleteBinLine, RiSendPlaneLine } from 'react-icons/ri';
import { API_BASE } from '../config';

/* ─────────────────────────────────────────────────────────────
   COLOR PALETTE
───────────────────────────────────────────────────────────── */
const T = {
  white: "#FFFFFF",
  offWhite: "#F4F7FF",
  softBlue: "#EBF1FF",
  primary: "#1A56DB",
  primaryDark: "#1240A8",
  primaryLight: "#3B82F6",
  navy: "#0F2557",
  navyDeep: "#091A3E",
  accent: "#60A5FA",
  accentGold: "#F59E0B",
  text: "#0F1F3D",
  textMid: "#374966",
  textMuted: "#6B7FA3",
  border: "#D6E4FF",
  borderLight: "#EBF1FF",
  shadow: "0 4px 24px rgba(26,86,219,0.08)",
  shadowMd: "0 8px 40px rgba(26,86,219,0.12)",
  shadowLg: "0 20px 60px rgba(26,86,219,0.16)",
  gradPrimary: "linear-gradient(135deg, #1A56DB 0%, #3B82F6 100%)",
  gradNavy: "linear-gradient(135deg, #0F2557 0%, #1A56DB 100%)",
  gradSoft: "linear-gradient(135deg, #EBF1FF 0%, #F4F7FF 100%)",
  gradGold: "linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)",
};

const MessageCenter = () => {
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/messages`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const messagesArray = Array.isArray(data) ? data : data.messages || [];
        setMessages(messagesArray);
      } else {
        setMessages(getDummyMessages());
      }
    } catch (error) {
      console.log('Failed to fetch messages, using dummy data');
      setMessages(getDummyMessages());
    } finally {
      setLoading(false);
    }
  };

  const getDummyMessages = () => {
    return [
      {
        _id: '1',
        name: 'John Doe',
        email: 'john@example.com',
        subject: 'Registration Question',
        message: 'Hi, I have a question about the registration process for the upcoming summit. Can you provide more details about the VIP package?',
        status: 'unread',
        createdAt: '2024-01-15T10:30:00Z'
      },
      {
        _id: '2',
        name: 'Jane Smith',
        email: 'jane@example.com',
        subject: 'Speaking Opportunity',
        message: 'I would like to inquire about speaking opportunities at Meetings21. I have extensive experience in AI and machine learning.',
        status: 'read',
        createdAt: '2024-01-14T14:20:00Z'
      },
      {
        _id: '3',
        name: 'Mike Johnson',
        email: 'mike@example.com',
        subject: 'Sponsorship Inquiry',
        message: 'Our company is interested in sponsoring the event. Please send us the sponsorship packages and pricing.',
        status: 'unread',
        createdAt: '2024-01-13T09:15:00Z'
      }
    ];
  };

  const markAsRead = (messageId) => {
    setMessages(messages.map(msg => 
      msg._id === messageId && msg.status === 'unread' 
        ? { ...msg, status: 'read' } 
        : msg
    ));
  };

  const handleMessageSelect = (message) => {
    setSelectedMessage(message);
    if (message.status === 'unread') {
      markAsRead(message._id);
    }
  };

  const handleReply = () => {
    if (replyText.trim()) {
      console.log('Replying to:', selectedMessage.email, 'Message:', replyText);
      alert(`Reply sent to ${selectedMessage.email}`);
      setReplyText('');
    }
  };

  const handleDelete = () => {
    if (selectedMessage) {
      setMessages(messages.filter(m => m._id !== selectedMessage._id));
      setSelectedMessage(null);
    }
  };

  const unreadCount = messages.filter(m => m.status === 'unread').length;

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '256px'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: `3px solid ${T.accentGold}20`,
          borderTopColor: T.accentGold,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '32px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <h1 style={{
            fontSize: '30px',
            fontWeight: 'bold',
            color: T.navy,
            marginBottom: '8px'
          }}>
            Message Center
          </h1>
          <p style={{ color: T.textMuted }}>
            {messages.length} messages • {unreadCount} unread
          </p>
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(1, 1fr)',
        gap: '24px',
      }}>
        <div style={{
          '@media (min-width: 1024px)': {
            gridTemplateColumns: 'repeat(3, 1fr)'
          }
        }}>
          {/* Message List */}
          <div style={{
            background: T.white,
            borderRadius: '16px',
            padding: '16px',
            maxHeight: '600px',
            overflowY: 'auto',
            boxShadow: T.shadowMd,
            border: `1px solid ${T.borderLight}`,
          }}>
            {messages.length === 0 ? (
              <div style={{
                padding: '32px',
                textAlign: 'center',
                color: T.textMuted
              }}>
                <RiMailOpenLine style={{ fontSize: '40px', margin: '0 auto 8px', display: 'block' }} />
                <p>No messages</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {messages.map((message) => (
                  <motion.div
                    key={message._id}
                    whileHover={{ x: 5 }}
                    onClick={() => handleMessageSelect(message)}
                    style={{
                      padding: '16px',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      background: selectedMessage?._id === message._id
                        ? T.softBlue
                        : message.status === 'unread'
                        ? T.offWhite
                        : 'transparent',
                      border: `1px solid ${
                        selectedMessage?._id === message._id
                          ? T.primaryLight
                          : 'transparent'
                      }`,
                    }}
                    whileHover={{
                      backgroundColor: T.softBlue,
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      marginBottom: '8px'
                    }}>
                      <h3 style={{
                        color: T.text,
                        fontWeight: 600,
                        fontSize: '14px'
                      }}>
                        {message.name}
                      </h3>
                      {message.status === 'unread' && (
                        <div style={{
                          width: '8px',
                          height: '8px',
                          backgroundColor: T.accentGold,
                          borderRadius: '50%'
                        }} />
                      )}
                    </div>
                    <p style={{
                      color: T.textMid,
                      fontSize: '13px',
                      marginBottom: '4px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {message.subject}
                    </p>
                    <p style={{
                      color: T.textMuted,
                      fontSize: '11px'
                    }}>
                      {message.createdAt ? new Date(message.createdAt).toLocaleDateString() : ''}
                    </p>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* Message Detail */}
          <div style={{
            background: T.white,
            borderRadius: '16px',
            padding: '24px',
            boxShadow: T.shadowMd,
            border: `1px solid ${T.borderLight}`,
          }}>
            {selectedMessage ? (
              <div>
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  marginBottom: '24px',
                  flexWrap: 'wrap',
                  gap: '16px'
                }}>
                  <div>
                    <h2 style={{
                      fontSize: '20px',
                      fontWeight: 'bold',
                      color: T.navy,
                      marginBottom: '8px'
                    }}>
                      {selectedMessage.subject}
                    </h2>
                    <p style={{ color: T.textMid }}>
                      From: {selectedMessage.name} ({selectedMessage.email})
                    </p>
                    <p style={{ color: T.textMuted, fontSize: '13px', marginTop: '4px' }}>
                      {selectedMessage.createdAt ? new Date(selectedMessage.createdAt).toLocaleString() : ''}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleReply}
                      style={{
                        padding: '8px',
                        background: T.softBlue,
                        color: T.primary,
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      title="Reply"
                    >
                      <RiReplyLine size={18} />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleDelete}
                      style={{
                        padding: '8px',
                        background: '#FEE2E2',
                        color: '#EF4444',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      title="Delete"
                    >
                      <RiDeleteBinLine size={18} />
                    </motion.button>
                  </div>
                </div>

                <div style={{
                  background: T.offWhite,
                  borderRadius: '12px',
                  padding: '24px',
                  marginBottom: '24px'
                }}>
                  <p style={{
                    color: T.text,
                    lineHeight: 1.6,
                    whiteSpace: 'pre-wrap'
                  }}>
                    {selectedMessage.message}
                  </p>
                </div>

                <div>
                  <textarea
                    placeholder="Type your reply..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      borderRadius: '8px',
                      color: T.text,
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      outline: 'none',
                      transition: 'all 0.2s',
                    }}
                    onFocus={(e) => e.currentTarget.style.borderColor = T.primary}
                    onBlur={(e) => e.currentTarget.style.borderColor = T.border}
                  />
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleReply}
                    style={{
                      marginTop: '12px',
                      padding: '10px 24px',
                      background: T.gradPrimary,
                      color: T.white,
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <RiSendPlaneLine size={16} />
                    Send Reply
                  </motion.button>
                </div>
              </div>
            ) : (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '400px',
                color: T.textMuted
              }}>
                <RiMailOpenLine style={{ fontSize: '64px', marginBottom: '16px' }} />
                <p style={{ fontSize: '18px' }}>Select a message to view</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Apply responsive grid */}
      <style>{`
        @media (min-width: 1024px) {
          .message-grid {
            display: grid;
            grid-template-columns: 1fr 2fr;
            gap: 24px;
          }
        }
      `}</style>
      <div className="message-grid">
        {/* The actual grid content is rendered above with conditional styling */}
      </div>
    </div>
  );
};

export default MessageCenter;